import { useState, useEffect } from "react";
import { Calendar, Clock, User, Building2, Mail, Phone, FileText, ChevronLeft, ChevronRight, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PrenotaPage = () => {
  const [step, setStep] = useState(1); // 1: select date, 2: select time, 3: fill form, 4: confirmation
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    notes: "",
    privacy_accepted: false
  });

  const timeSlots = [
    { time: "09:00", label: "09:00 - 10:00" },
    { time: "10:00", label: "10:00 - 11:00" },
    { time: "11:00", label: "11:00 - 12:00" },
    { time: "12:00", label: "12:00 - 13:00" },
    { time: "13:00", label: "13:00 - 14:00" },
    { time: "14:00", label: "14:00 - 15:00" },
    { time: "15:00", label: "15:00 - 16:00" },
    { time: "16:00", label: "16:00 - 17:00" },
  ];

  const dayNames = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
  const monthNames = [
    "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
  ];

  // Fetch available slots when date is selected
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const dateStr = formatDateForAPI(date);
      const response = await axios.get(`${API}/bookings/available/${dateStr}`);
      setAvailableSlots(response.data.available_slots);
    } catch (error) {
      console.error("Error fetching slots:", error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDateDisplay = (date) => {
    return `${dayNames[date.getDay()]} ${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const isDateSelectable = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Must be a weekday (Mon-Fri)
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) return false;
    
    // Must be today or in the future
    if (date < today) return false;
    
    return true;
  };

  const handleDateSelect = (date) => {
    if (isDateSelectable(date)) {
      setSelectedDate(date);
      setSelectedTime(null);
      setStep(2);
    }
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setStep(3);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.privacy_accepted) {
      toast.error("Devi accettare la privacy policy per procedere");
      return;
    }

    setIsSubmitting(true);

    try {
      const bookingData = {
        ...formData,
        date: formatDateForAPI(selectedDate),
        time: selectedTime
      };
      
      const response = await axios.post(`${API}/bookings`, bookingData);
      setBookingConfirmed(response.data);
      setStep(4);
      toast.success("Prenotazione confermata!");
    } catch (error) {
      console.error("Error creating booking:", error);
      const message = error.response?.data?.detail || "Errore durante la prenotazione. Riprova.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const resetBooking = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedTime(null);
    setBookingConfirmed(null);
    setFormData({
      name: "",
      company: "",
      email: "",
      phone: "",
      notes: "",
      privacy_accepted: false
    });
  };

  return (
    <div data-testid="prenota-page">
      {/* Hero Section */}
      <section className="bg-[#f5f6fa] py-12 md:py-16">
        <div className="section-container">
          <div className="max-w-3xl mx-auto text-center">
            <span className="badge mb-4">Prenota una Demo</span>
            <h1 className="heading-2 text-[#1a1f36] mb-4">
              Scopri iSiAP dal vivo
            </h1>
            <p className="body-large">
              Prenota una demo gratuita di 1 ora con un nostro esperto. 
              Ti mostreremo come iSiAP può semplificare la gestione della tua attività.
            </p>
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section className="section-padding">
        <div className="section-container">
          <div className="max-w-4xl mx-auto">
            {/* Progress Steps */}
            <div className="flex items-center justify-center mb-12">
              {[1, 2, 3, 4].map((s, index) => (
                <div key={s} className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                      step >= s
                        ? "bg-[#FF6B00] text-white"
                        : "bg-[#e2e4ed] text-[#7a7f94]"
                    }`}
                  >
                    {step > s ? <CheckCircle2 size={20} /> : s}
                  </div>
                  {index < 3 && (
                    <div
                      className={`w-16 md:w-24 h-1 mx-2 transition-all ${
                        step > s ? "bg-[#FF6B00]" : "bg-[#e2e4ed]"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Step Labels */}
            <div className="flex justify-between mb-8 text-sm text-[#7a7f94] px-4">
              <span className={step >= 1 ? "text-[#FF6B00] font-medium" : ""}>Data</span>
              <span className={step >= 2 ? "text-[#FF6B00] font-medium" : ""}>Orario</span>
              <span className={step >= 3 ? "text-[#FF6B00] font-medium" : ""}>Dati</span>
              <span className={step >= 4 ? "text-[#FF6B00] font-medium" : ""}>Conferma</span>
            </div>

            <div className="bg-white rounded-2xl border border-[#e2e4ed] p-6 md:p-10 shadow-sm">
              {/* Step 1: Select Date */}
              {step === 1 && (
                <div data-testid="step-date">
                  <div className="flex items-center gap-3 mb-6">
                    <Calendar size={24} className="text-[#FF6B00]" />
                    <h2 className="text-xl font-bold text-[#1a1f36]">Seleziona una data</h2>
                  </div>
                  <p className="text-[#4a5068] mb-6">
                    Disponibilità: Lunedì - Venerdì
                  </p>

                  {/* Calendar */}
                  <div className="max-w-md mx-auto">
                    <div className="flex items-center justify-between mb-4">
                      <button
                        onClick={prevMonth}
                        className="p-2 rounded-lg hover:bg-[#f5f6fa] transition-colors"
                        data-testid="prev-month"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <h3 className="text-lg font-semibold text-[#1a1f36]">
                        {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                      </h3>
                      <button
                        onClick={nextMonth}
                        className="p-2 rounded-lg hover:bg-[#f5f6fa] transition-colors"
                        data-testid="next-month"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>

                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {dayNames.map((day) => (
                        <div key={day} className="text-center text-sm font-medium text-[#7a7f94] py-2">
                          {day}
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-7 gap-1">
                      {getDaysInMonth(currentMonth).map((date, index) => (
                        <button
                          key={index}
                          onClick={() => date && handleDateSelect(date)}
                          disabled={!isDateSelectable(date)}
                          className={`
                            aspect-square rounded-lg text-sm font-medium transition-all
                            ${!date ? "invisible" : ""}
                            ${isDateSelectable(date)
                              ? "hover:bg-[#FF6B00] hover:text-white cursor-pointer"
                              : "text-[#ccc] cursor-not-allowed"
                            }
                            ${selectedDate && date && selectedDate.toDateString() === date.toDateString()
                              ? "bg-[#FF6B00] text-white"
                              : "text-[#1a1f36]"
                            }
                          `}
                          data-testid={date ? `date-${formatDateForAPI(date)}` : undefined}
                        >
                          {date?.getDate()}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Select Time */}
              {step === 2 && (
                <div data-testid="step-time">
                  <div className="flex items-center gap-3 mb-6">
                    <Clock size={24} className="text-[#FF6B00]" />
                    <h2 className="text-xl font-bold text-[#1a1f36]">Seleziona un orario</h2>
                  </div>
                  
                  <div className="mb-6 p-4 bg-[#f5f6fa] rounded-xl">
                    <p className="text-[#4a5068]">
                      Data selezionata: <span className="font-semibold text-[#1a1f36]">{formatDateDisplay(selectedDate)}</span>
                    </p>
                  </div>

                  {loadingSlots ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 size={32} className="animate-spin text-[#FF6B00]" />
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-[#7a7f94] mb-4">Nessuno slot disponibile per questa data.</p>
                      <button
                        onClick={() => setStep(1)}
                        className="text-[#FF6B00] font-medium hover:underline"
                      >
                        Seleziona un'altra data
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {timeSlots.map((slot) => {
                        const isAvailable = availableSlots.includes(slot.time);
                        return (
                          <button
                            key={slot.time}
                            onClick={() => isAvailable && handleTimeSelect(slot.time)}
                            disabled={!isAvailable}
                            className={`
                              p-4 rounded-xl border-2 text-center transition-all
                              ${isAvailable
                                ? "border-[#e2e4ed] hover:border-[#FF6B00] hover:bg-[#FF6B00]/5 cursor-pointer"
                                : "border-[#e2e4ed] bg-[#f5f6fa] text-[#ccc] cursor-not-allowed line-through"
                              }
                              ${selectedTime === slot.time ? "border-[#FF6B00] bg-[#FF6B00]/10" : ""}
                            `}
                            data-testid={`time-${slot.time}`}
                          >
                            <p className="font-semibold">{slot.label}</p>
                            {isAvailable ? (
                              <p className="text-xs text-green-600 mt-1">Disponibile</p>
                            ) : (
                              <p className="text-xs text-[#999] mt-1">Non disponibile</p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div className="mt-8 flex justify-between">
                    <button
                      onClick={() => setStep(1)}
                      className="px-6 py-3 rounded-lg border-2 border-[#e2e4ed] text-[#4a5068] font-medium hover:bg-[#f5f6fa] transition-colors"
                    >
                      Indietro
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Fill Form */}
              {step === 3 && (
                <div data-testid="step-form">
                  <div className="flex items-center gap-3 mb-6">
                    <User size={24} className="text-[#FF6B00]" />
                    <h2 className="text-xl font-bold text-[#1a1f36]">I tuoi dati</h2>
                  </div>

                  <div className="mb-6 p-4 bg-[#f5f6fa] rounded-xl">
                    <p className="text-[#4a5068]">
                      <span className="font-semibold text-[#1a1f36]">{formatDateDisplay(selectedDate)}</span>
                      {" "}alle{" "}
                      <span className="font-semibold text-[#1a1f36]">{selectedTime} - {parseInt(selectedTime) + 1}:00</span>
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-[#1a1f36] mb-2">
                          Nome e Cognome *
                        </label>
                        <div className="relative">
                          <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7a7f94]" />
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="input-style pl-11"
                            placeholder="Mario Rossi"
                            data-testid="input-name"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#1a1f36] mb-2">
                          Azienda
                        </label>
                        <div className="relative">
                          <Building2 size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7a7f94]" />
                          <input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="input-style pl-11"
                            placeholder="Nome azienda"
                            data-testid="input-company"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-[#1a1f36] mb-2">
                          Email *
                        </label>
                        <div className="relative">
                          <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7a7f94]" />
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="input-style pl-11"
                            placeholder="mario@esempio.it"
                            data-testid="input-email"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#1a1f36] mb-2">
                          Telefono *
                        </label>
                        <div className="relative">
                          <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7a7f94]" />
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className="input-style pl-11"
                            placeholder="+39 333 1234567"
                            data-testid="input-phone"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#1a1f36] mb-2">
                        Note (opzionale)
                      </label>
                      <div className="relative">
                        <FileText size={18} className="absolute left-4 top-4 text-[#7a7f94]" />
                        <textarea
                          name="notes"
                          value={formData.notes}
                          onChange={handleChange}
                          className="textarea-style pl-11 min-h-[100px]"
                          placeholder="Dicci di più sulle tue esigenze..."
                          data-testid="input-notes"
                        />
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="privacy_accepted"
                        name="privacy_accepted"
                        checked={formData.privacy_accepted}
                        onChange={handleChange}
                        className="mt-1 w-4 h-4 accent-[#FF6B00]"
                        data-testid="checkbox-privacy"
                      />
                      <label htmlFor="privacy_accepted" className="text-sm text-[#4a5068]">
                        Dichiaro di aver letto e accettato i{" "}
                        <a href="/termini" className="text-[#FF6B00] underline">Termini e Condizioni</a>
                        {" "}e l'{" "}
                        <a href="/privacy" className="text-[#FF6B00] underline">Informativa sulla Privacy</a>. *
                      </label>
                    </div>

                    <div className="flex justify-between pt-4">
                      <button
                        type="button"
                        onClick={() => setStep(2)}
                        className="px-6 py-3 rounded-lg border-2 border-[#e2e4ed] text-[#4a5068] font-medium hover:bg-[#f5f6fa] transition-colors"
                      >
                        Indietro
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary"
                        data-testid="submit-booking"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 size={18} className="animate-spin" />
                            Invio in corso...
                          </>
                        ) : (
                          "Conferma Prenotazione"
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Step 4: Confirmation */}
              {step === 4 && bookingConfirmed && (
                <div data-testid="step-confirmation" className="text-center py-8">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 size={40} className="text-green-600" />
                  </div>
                  
                  <h2 className="text-2xl font-bold text-[#1a1f36] mb-4">
                    Prenotazione Confermata!
                  </h2>
                  
                  <p className="text-[#4a5068] mb-8 max-w-md mx-auto">
                    Grazie {bookingConfirmed.name}! La tua demo è stata prenotata con successo. 
                    Riceverai a breve un'email di conferma.
                  </p>

                  <div className="bg-[#f5f6fa] rounded-xl p-6 max-w-md mx-auto mb-8">
                    <h3 className="font-semibold text-[#1a1f36] mb-4">Riepilogo Prenotazione</h3>
                    <div className="space-y-3 text-left">
                      <div className="flex justify-between">
                        <span className="text-[#7a7f94]">Data:</span>
                        <span className="font-medium text-[#1a1f36]">{formatDateDisplay(selectedDate)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#7a7f94]">Orario:</span>
                        <span className="font-medium text-[#1a1f36]">{bookingConfirmed.time} - {parseInt(bookingConfirmed.time) + 1}:00</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#7a7f94]">Email:</span>
                        <span className="font-medium text-[#1a1f36]">{bookingConfirmed.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#7a7f94]">ID Prenotazione:</span>
                        <span className="font-mono text-sm text-[#7a7f94]">{bookingConfirmed.id.slice(0, 8)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <p className="text-sm text-[#7a7f94]">
                      Hai domande? Contattaci a <a href="mailto:info@isiap.it" className="text-[#FF6B00]">info@isiap.it</a>
                    </p>
                    <button
                      onClick={resetBooking}
                      className="text-[#FF6B00] font-medium hover:underline"
                    >
                      Prenota un'altra demo
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrenotaPage;
